'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { Skeleton } from '@shared/components/shadcn';
import { twMerge } from '@shared/themes';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { Fragment, Suspense, lazy, type ComponentProps } from 'react';
import type { PageSectionsResponse, PageSlug, Pod, Section, SectionArea } from './types';

const LotteryStageTemplate = lazy(() => import('./templates/LotteryStageTemplate/LotteryStageTemplate').then(mod => ({ default: mod.LotteryStageTemplate })));
const HomepageLottoCarouselTemplate = lazy(() =>
  import('./templates/HomepageLottoCarouselTemplate/HomepageLottoCarouselTemplate').then(mod => ({ default: mod.HomepageLottoCarouselTemplate }))
);
const OfferHeroBannerTemplate = lazy(() => import('./templates/OfferHeroBannerTemplate/OfferHeroBannerTemplate').then(mod => ({ default: mod.OfferHeroBannerTemplate })));
const StageHeadlineTemplate = lazy(() => import('./templates/StageHeadlineTemplate/StageHeadlineTemplate').then(mod => ({ default: mod.StageHeadlineTemplate })));
const SportsbookBannerCarouselTemplate = lazy(() =>
  import('./templates/SportsbookBannerCarouselTemplate/SportsbookBannerCarouselTemplate').then(mod => ({ default: mod.SportsbookBannerCarouselTemplate }))
);
const GameOfTheWeekTemplate = lazy(() => import('./templates/GameOfTheWeekTemplate/GameOfTheWeekTemplate').then(mod => ({ default: mod.GameOfTheWeekTemplate })));
const ScratchcardOfTheMonthTemplate = lazy(() =>
  import('./templates/ScratchcardOfTheMonthTemplate/ScratchcardOfTheMonthTemplate').then(mod => ({ default: mod.ScratchcardOfTheMonthTemplate }))
);
const GamesCarouselTemplate = lazy(() => import('./templates/GamesCarouselTemplate/GamesCarouselTemplate').then(mod => ({ default: mod.GamesCarouselTemplate })));

type AreaComponentProps = ComponentProps<'div'> & {
  slug: PageSlug;
  area: SectionArea;
  dataTrackStageProduct?: string;
};

function mapPodsWithResolvedCallToAction(pods: Pod[], isLoggedIn: boolean, offerHeroLoginLabel: string): Pod[] {
  return pods.map(pod => {
    const cta = pod.callToAction;
    if (!cta) {
      return pod;
    }
    const getRegisterUrl = (code?: string) => (code ? `${ROUTES.REGISTER}?code=${code}` : ROUTES.REGISTER);

    return {
      ...pod,
      callToAction: {
        text: isLoggedIn ? (cta.text ?? '') : offerHeroLoginLabel,
        link: isLoggedIn ? (cta.link ?? '') : getRegisterUrl(cta.code),
      },
    };
  });
}

function SectionHeader({ title, description, className }: { title?: string; description?: string; className?: string }) {
  if (!title && !description) return null;

  return (
    <div className={className}>
      {title && <h1 className="heading-4">{title}</h1>}
      {description && <p className="text-foreground-subtle">{description}</p>}
    </div>
  );
}

function renderSection(section: Section, dataTrackStageProduct: string | undefined, isLoggedIn: boolean, offerHeroLoginLabel: string, slug: PageSlug): React.ReactNode {
  const pods = mapPodsWithResolvedCallToAction(section.pods, isLoggedIn, offerHeroLoginLabel);

  switch (section.template) {
    case 'HERO_BANNER':
      return pods.map(pod => (
        <OfferHeroBannerTemplate
          key={pod.id}
          className="ll-container-full lg:ll-container-inner"
          title={pod.title}
          buttonText={pod.callToAction?.text ?? ''}
          buttonHref={pod.callToAction?.link ?? ''}
          tc={pod.highLevelTermsAndConditions}
          image={pod.image?.link}
          altText={pod.image?.altText}
          callout={pod.callout}
          data-testid="area-component-hero-banner-cta"
        />
      ));

    case 'BANNER':
      return pods.map(pod => (
        <StageHeadlineTemplate
          key={pod.id}
          className="ll-container-inner"
          title={pod.title}
          buttonText={pod.callToAction?.text ?? ''}
          buttonHref={pod.callToAction?.link ?? ''}
          tc={pod.highLevelTermsAndConditions ?? ''}
          image={pod.image?.link ?? ''}
          altText={pod.image?.altText}
          callout={pod.callout}
          data-track-stage-product={dataTrackStageProduct}
        />
      ));

    case 'FULL_WIDTH_LOTTERY': {
      const lotteryPods = pods.filter(pod => pod.lotteryId);
      if (lotteryPods.length === 0) return null;

      return (
        <>
          <SectionHeader title={section.title} description={section.description} className="ll-container-inner" />
          {lotteryPods.map(pod => (
            <LotteryStageTemplate key={pod.id} className="ll-container-inner" lotteryName={pod.lotteryId!} namespace="home.stage" />
          ))}
        </>
      );
    }

    case 'LOTTERIES_CAROUSEL': {
      const lotteries = pods.flatMap(pod => pod.lotteryId ?? []);
      if (lotteries.length === 0) return null;
      return <HomepageLottoCarouselTemplate className="ll-container-full" lotteries={lotteries} title={section.title} />;
    }

    case 'BANNER_CAROUSEL': {
      if (pods.length === 0) return null;
      return <SportsbookBannerCarouselTemplate className="ll-container-full" pods={pods} title={section.title} data-testid="area-component-banner-carousel" />;
    }

    case 'GAME_OF_THE_WEEK': {
      const gamePods = section.pods.filter(pod => pod.gameId);
      if (gamePods.length === 0) return null;

      return gamePods.map(pod => <GameOfTheWeekTemplate key={pod.id} className="ll-container-inner" namespace="casino.discover_page.game_of_the_week" gameName={pod.gameId!} />);
    }

    case 'SCRATCHCARD_OF_THE_MONTH': {
      const scratchcardPods = section.pods.filter(pod => pod.gameId);
      if (scratchcardPods.length === 0) return null;

      return scratchcardPods.map(pod => (
        <ScratchcardOfTheMonthTemplate key={pod.id} className="ll-container-full" namespace="scratchcards.discover_page.scratchcard_of_the_month" scratchcardName={pod.gameId!} />
      ));
    }

    case 'GAMES_CAROUSEL': {
      const carouselPods = section.pods.filter(pod => pod.gamesCategoryId || pod.optixPlacementId);
      if (carouselPods.length === 0) return null;

      return carouselPods.map(pod => (
        <GamesCarouselTemplate
          key={pod.id}
          className="ll-container-full"
          title={pod.title}
          gamesCategoryId={pod.gamesCategoryId}
          gamesCategorySlug={pod.gamesCategorySlug}
          optixPlacementId={pod.optixPlacementId}
          useThirdPartyRecommendations={pod.useThirdPartyRecommendations}
          vertical={slug === 'live-casino' ? 'live-casino' : 'casino'}
        />
      ));
    }

    default:
      return null;
  }
}

function AreaComponentSkeleton({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div className={twMerge('ll-container-inner flex flex-col gap-4', className)} data-testid="area-skeleton" {...props}>
      <Skeleton className="h-6 w-60" />
      <Skeleton className="h-60 w-full rounded-lg md:h-80" />
    </div>
  );
}

function AreaComponent({ slug, area, dataTrackStageProduct, className, ...props }: AreaComponentProps) {
  const { session } = useSession();
  const isLoggedIn = session.status === 'authenticated';
  const t = useTranslations('home.offer_hero_banner');
  const offerHeroJoinLabel = t('cta');

  const pageSlug = slug;
  const route = isLoggedIn ? API_ROUTES.MARKETING.PAGE_SECTIONS_PRIVATE.replace(':slug', pageSlug) : API_ROUTES.MARKETING.PAGE_SECTIONS.replace(':slug', pageSlug);

  const { data, isLoading } = useApiQuery<PageSectionsResponse>(route, {
    params: { area },
    credentials: isLoggedIn,
  });

  if (isLoading) return <AreaComponentSkeleton />;

  if (!data?.list?.length) return null;

  return (
    <Suspense fallback={<AreaComponentSkeleton />}>
      <div className={twMerge('[&>*+*]:mt-5', className, 'll-grid-container')} {...props}>
        {data.list.map((section: Section) => {
          const content = renderSection(section, dataTrackStageProduct, isLoggedIn, offerHeroJoinLabel, slug);
          if (!content) return null;

          return <Fragment key={section.id}>{content}</Fragment>;
        })}
      </div>
    </Suspense>
  );
}

export { AreaComponent, AreaComponentSkeleton };
